import { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import { Navigation2 } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, mapsService } from '../services/api';
import type { LiveCarrierLocation } from '../hooks/useSignalR';
import type { RelaySegmentDto } from '../types';
import { MAP_STYLE_URL, createPinElement, decodePolyline5 } from '../lib/mapLibre';

const SEG_COLOURS: Record<number, string> = {
  0: '#94a3b8', 1: '#047857', 2: '#f59e0b',
  3: '#8b5cf6', 4: '#10b981', 5: '#22c55e', 6: '#ef4444',
};
const SEG_LABELS = ['Pending','Assigned','Picked Up','In Transit','Handed Over','Completed','Failed'];

const SEGMENTS_SOURCE = 'relay-segments';

interface RelayMapProps {
  segments: RelaySegmentDto[];
  pickupLat: number; pickupLng: number;
  deliveryLat: number; deliveryLng: number;
}

export function RelayMap({ segments, pickupLat, pickupLng, deliveryLat, deliveryLng }: RelayMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [pickupLng, pickupLat],
      zoom: 6,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      const bounds = new maplibregl.LngLatBounds([pickupLng, pickupLat], [pickupLng, pickupLat]);

      new maplibregl.Marker({ element: createPinElement('P', '#16a34a') })
        .setLngLat([pickupLng, pickupLat])
        .setPopup(new maplibregl.Popup().setHTML('<b>Pickup</b>'))
        .addTo(map);
      bounds.extend([pickupLng, pickupLat]);

      const features: Feature[] = [];
      segments.forEach((seg, i) => {
        const col  = SEG_COLOURS[seg.status] ?? '#94a3b8';
        const from: [number, number] = [seg.pickupLng ?? pickupLng, seg.pickupLat ?? pickupLat];
        const to:   [number, number] = [seg.deliveryLng ?? deliveryLng, seg.deliveryLat ?? deliveryLat];

        features.push({
          type: 'Feature', properties: { color: col, dashed: seg.status === 0 },
          geometry: { type: 'LineString', coordinates: [from, to] },
        });

        const popupHtml =
          `<b>Segment ${seg.segmentOrder}</b><br>` +
          `${seg.distanceMiles.toFixed(1)} mi · £${seg.totalRelayFee.toFixed(2)}<br>` +
          `<span style="color:${col}">${SEG_LABELS[seg.status] ?? ''}</span>` +
          (seg.assignedCarrierName ? `<br>${seg.assignedCarrierName}` : '');

        new maplibregl.Marker({ element: createPinElement(String(i + 1), col) })
          .setLngLat(from)
          .setPopup(new maplibregl.Popup().setHTML(popupHtml))
          .addTo(map);

        bounds.extend(from); bounds.extend(to);
      });

      map.addSource(SEGMENTS_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features } });
      map.addLayer({
        id: SEGMENTS_SOURCE, type: 'line', source: SEGMENTS_SOURCE,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
          'line-opacity': 0.85,
          'line-dasharray': ['case', ['get', 'dashed'], ['literal', [2, 1]], ['literal', [1, 0]]],
        },
      });

      new maplibregl.Marker({ element: createPinElement('D', '#dc2626') })
        .setLngLat([deliveryLng, deliveryLat])
        .setPopup(new maplibregl.Popup().setHTML('<b>Delivery</b>'))
        .addTo(map);
      bounds.extend([deliveryLng, deliveryLat]);

      map.fitBounds(bounds, { padding: 40 });
      setTimeout(() => map.resize(), 150);
    });

    return () => { mapRef.current?.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation2 className="w-4 h-4 text-purple-600" />
          <span className="font-medium text-gray-900 text-sm">
            Relay Route — {segments.length} segment{segments.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span><span className="inline-block w-3 h-0.5 bg-gray-400 mr-1" style={{ borderTop: '2px dashed #94a3b8' }} />Pending</span>
          <span><span className="inline-block w-3 h-0.5 bg-blue-500 mr-1" />Active</span>
          <span><span className="inline-block w-3 h-0.5 bg-green-500 mr-1" />Complete</span>
        </div>
      </div>

      <div ref={containerRef} className="h-72 w-full" />

      {/* Segment strip */}
      <div className="px-4 py-3 border-t border-gray-100 flex gap-2 overflow-x-auto">
        {segments.map(seg => (
          <div key={seg.id} className="shrink-0 flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs">
            <span className="w-3 h-3 rounded-full shrink-0"
              style={{ background: SEG_COLOURS[seg.status] ?? '#94a3b8' }} />
            <div>
              <span className="font-medium text-gray-700">Seg {seg.segmentOrder}</span>
              <span className="text-gray-400 ml-1.5">{seg.distanceMiles.toFixed(0)}mi</span>
              <span className="text-green-600 ml-1.5 font-medium">£{seg.totalRelayFee.toFixed(2)}</span>
            </div>
            {seg.assignedCarrierName && <span className="text-gray-500">{seg.assignedCarrierName}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtAge(d: Date): string {
  const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
  return mins < 1 ? 'just now' : `${mins}m ago`;
}

/**
 * LiveTrackingMap — shows a static pickup→delivery route plus a live carrier dot.
 * Used by both the Carrier and Customer views for in-progress delivery jobs.
 * The caller supplies carrier location updates via `carrierLocation` prop (driven by
 * the CarrierLocationUpdated SignalR event from useJobHub).
 */
export function LiveTrackingMap({
  pickupLat, pickupLng, pickupAddress,
  deliveryLat, deliveryLng, deliveryAddress,
  carrierLocation,
}: {
  pickupLat: number; pickupLng: number; pickupAddress: string;
  deliveryLat: number; deliveryLng: number; deliveryAddress: string;
  carrierLocation: LiveCarrierLocation | null;
}) {
  const { getToken } = useAppAuth();
  const containerRef   = useRef<HTMLDivElement>(null);
  const mapRef         = useRef<maplibregl.Map | null>(null);
  const carrierMarker  = useRef<maplibregl.Marker | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [eta, setEta] = useState<{ mins: number; distMi: number } | null>(null);
  const [, tick] = useState(0);

  // Build the static route + base markers once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let active = true;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [pickupLng, pickupLat],
      zoom: 8,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      if (!active) return;

      new maplibregl.Marker({ element: createPinElement('P', '#16a34a') })
        .setLngLat([pickupLng, pickupLat])
        .setPopup(new maplibregl.Popup().setHTML(`<b>Pickup</b><br>${pickupAddress}`))
        .addTo(map);
      new maplibregl.Marker({ element: createPinElement('D', '#dc2626') })
        .setLngLat([deliveryLng, deliveryLat])
        .setPopup(new maplibregl.Popup().setHTML(`<b>Delivery</b><br>${deliveryAddress}`))
        .addTo(map);

      const bounds = new maplibregl.LngLatBounds([pickupLng, pickupLat], [pickupLng, pickupLat]);
      bounds.extend([deliveryLng, deliveryLat]);
      map.fitBounds(bounds, { padding: 40 });
      setTimeout(() => { if (active) { map.resize(); map.fitBounds(bounds, { padding: 40 }); } }, 200);

      map.addSource('live-tracking-route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'live-tracking-route', type: 'line', source: 'live-tracking-route',
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': '#047857', 'line-width': 4, 'line-opacity': 0.9 },
      });

      // Planning route (free, self-hosted Valhalla) - just for drawing the road-following
      // line, not the live ETA (that's the separate live-route call below).
      mapsService(createApiClient(getToken)).getRoute(pickupLat, pickupLng, deliveryLat, deliveryLng)
        .then(route => {
          if (!active || !route.encodedPolyline) return;
          const coords = decodePolyline5(route.encodedPolyline);
          (map.getSource('live-tracking-route') as maplibregl.GeoJSONSource)?.setData({
            type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords },
          });
        })
        .catch(() => {});
    });

    return () => { active = false; mapRef.current?.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update carrier marker whenever location changes
  useEffect(() => {
    if (!carrierLocation || !mapRef.current) return;
    const map = mapRef.current;

    if (carrierMarker.current) {
      carrierMarker.current.setLngLat([carrierLocation.lng, carrierLocation.lat]);
    } else {
      const el = document.createElement('div');
      el.style.cssText =
        'background:#2563eb;color:white;border-radius:50%;width:28px;height:28px;' +
        'display:flex;align-items:center;justify-content:center;font-size:14px;' +
        'border:3px solid white;box-shadow:0 2px 6px rgba(37,99,235,.5)';
      el.textContent = '🚚';
      carrierMarker.current = new maplibregl.Marker({ element: el })
        .setLngLat([carrierLocation.lng, carrierLocation.lat])
        .setPopup(new maplibregl.Popup().setText('Carrier — live location'))
        .addTo(map);
    }
    setLastUpdated(new Date(carrierLocation.recordedAt));
    tick(n => n + 1);
  }, [carrierLocation]);

  // Compute traffic-aware ETA whenever the carrier moves (TomTom, server-side, metered)
  useEffect(() => {
    if (!carrierLocation) return;
    mapsService(createApiClient(getToken))
      .getLiveRoute(carrierLocation.lat, carrierLocation.lng, deliveryLat, deliveryLng)
      .then(r => setEta({ mins: r.estimatedMinutes, distMi: r.distanceMiles }))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierLocation, deliveryLat, deliveryLng]);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation2 className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-900 text-sm">Live Tracking</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            Live
          </span>
          {eta && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              ~{eta.mins} min · {eta.distMi.toFixed(1)} mi
            </span>
          )}
          {lastUpdated && (
            <span className="text-xs text-gray-400">{fmtAge(lastUpdated)}</span>
          )}
          {!carrierLocation && (
            <span className="text-xs text-gray-400">Awaiting carrier GPS…</span>
          )}
        </div>
      </div>
      <div ref={containerRef} className="h-72 w-full" />
    </div>
  );
}

/** Road-following route map. live=true for same-day jobs (refreshes every 15 min), false for scheduled (fetched once). */
export function RouteMap({
  pickupLat, pickupLng, pickupAddress,
  deliveryLat, deliveryLng, deliveryAddress,
  live = false,
}: {
  pickupLat: number; pickupLng: number; pickupAddress: string;
  deliveryLat: number; deliveryLng: number; deliveryAddress: string;
  live?: boolean;
}) {
  const { getToken } = useAppAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [routeStats, setRouteStats] = useState<{ durationMins: number; distMiles: number } | null>(null);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let active = true;
    let routeInterval: ReturnType<typeof setInterval> | null = null;
    let tickInterval:  ReturnType<typeof setInterval> | null = null;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [pickupLng, pickupLat],
      zoom: 8,
    });
    mapRef.current = map;

    map.on('load', () => {
      if (!active) return;

      new maplibregl.Marker({ element: createPinElement('P', '#16a34a') })
        .setLngLat([pickupLng, pickupLat])
        .setPopup(new maplibregl.Popup().setHTML(`<b>Pickup</b><br>${pickupAddress}`))
        .addTo(map);
      new maplibregl.Marker({ element: createPinElement('D', '#dc2626') })
        .setLngLat([deliveryLng, deliveryLat])
        .setPopup(new maplibregl.Popup().setHTML(`<b>Delivery</b><br>${deliveryAddress}`))
        .addTo(map);

      const bounds = new maplibregl.LngLatBounds([pickupLng, pickupLat], [pickupLng, pickupLat]);
      bounds.extend([deliveryLng, deliveryLat]);
      map.fitBounds(bounds, { padding: 40 });

      // Leaflet-era comment still applies: the container can be mid-layout-pass at mount time,
      // so re-invalidate + re-fit after the browser has finished layout.
      setTimeout(() => {
        if (!active) return;
        map.resize();
        map.fitBounds(bounds, { padding: 40 });
      }, 200);

      map.addSource('route-map-line', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'route-map-line', type: 'line', source: 'route-map-line',
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': '#047857', 'line-width': 4, 'line-opacity': 0.9 },
      });

      const drawRoute = async () => {
        if (!active) return;
        try {
          const route = await mapsService(createApiClient(getToken))
            .getRoute(pickupLat, pickupLng, deliveryLat, deliveryLng);
          if (!active || !route.encodedPolyline) return;

          const coords = decodePolyline5(route.encodedPolyline);
          (map.getSource('route-map-line') as maplibregl.GeoJSONSource)?.setData({
            type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords },
          });
          const routeBounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
          map.fitBounds(routeBounds, { padding: 30 });
          if (active) {
            setLastUpdated(new Date());
            setRouteStats({ durationMins: route.estimatedMinutes, distMiles: route.distanceMiles });
          }
        } catch { /* network error — keep previous route */ }
      };

      drawRoute();

      if (live) {
        routeInterval = setInterval(drawRoute, 15 * 60 * 1000);
        tickInterval  = setInterval(() => tick(n => n + 1), 60_000);
      }
    });

    return () => {
      active = false;
      if (routeInterval) clearInterval(routeInterval);
      if (tickInterval)  clearInterval(tickInterval);
      mapRef.current?.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation2 className="w-4 h-4 text-green-600" />
          <span className="font-medium text-gray-900 text-sm">Route Directions</span>
        </div>
        <div className="flex items-center gap-2">
          {live ? (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              ⚡ Live{lastUpdated ? ` · ${fmtAge(lastUpdated)}` : ''}
            </span>
          ) : (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
              Saved route
            </span>
          )}
          {routeStats && (
            <span className="text-xs text-gray-500">
              ~{routeStats.durationMins} min · {routeStats.distMiles.toFixed(1)} mi
            </span>
          )}
        </div>
      </div>
      <div ref={containerRef} className="h-56 w-full" />
    </div>
  );
}
